import { useState, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

// material-ui
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';

// project-imports
import useAuth from 'hooks/useAuth';
import { useAccounts } from 'api/vektra/auth';
import { ApiError } from 'api/vektra/client';
import { openSnackbar } from 'api/snackbar';
import { isImpersonationSession } from 'utils/impersonation';
import type { SnackbarProps } from 'types/snackbar';
import type { AccountMembershipResponse, UserRole } from 'api/vektra/types';

// assets
import { ArrowDown2, Buildings } from 'iconsax-reactjs';

// ==============================|| HEADER CONTENT - ACCOUNT SWITCHER ||============================== //

// VEK-60 (ACCT-01) — the active business account, and the way into the user's other ones. One
// account is a plain label; two or more open a menu. Platform administrators keep their login-time
// account selection, and an impersonation tab never switches: the API cannot tell an impersonated
// token apart, so the switch would carry the impersonator into accounts they are not a member of.
const PLATFORM_ROLES: UserRole[] = ['SUPER_ADMIN', 'ADMIN'];

export default function AccountSwitcher() {
  const { user } = useAuth() as any;
  if (!user?.businessAccountId || PLATFORM_ROLES.includes(user.role as UserRole) || isImpersonationSession()) {
    return null;
  }
  return <AccountSwitcherControl activeAccountId={user.businessAccountId} />;
}

function AccountSwitcherControl({ activeAccountId }: { activeAccountId: string }) {
  const { t } = useTranslation();
  const { switchAccount } = useAuth() as any;
  const { data: accounts } = useAccounts();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [switching, setSwitching] = useState(false);

  const active = accounts?.find((a) => a.businessAccountId === activeAccountId);
  // Nothing until the list has loaded, so a wrong or empty name never flashes.
  if (!active) return null;

  const others = (accounts ?? []).filter((a) => a.businessAccountId !== activeAccountId);
  const roleLabel = (a: AccountMembershipResponse) =>
    t(`accountSwitcher.role.${a.role}`, { defaultValue: a.role.replace(/_/g, ' ').toLowerCase() });

  const handleChoose = async (account: AccountMembershipResponse) => {
    setAnchorEl(null);
    setSwitching(true);
    try {
      await switchAccount(account.businessAccountId);
      // The page is being replaced; leave the control disabled until it is.
    } catch (err) {
      setSwitching(false);
      openSnackbar({
        open: true,
        message:
          err instanceof ApiError && err.serverMessage
            ? err.serverMessage
            : t('accountSwitcher.failed', { defaultValue: 'Could not switch business account' }),
        variant: 'alert',
        severity: 'error'
      } as SnackbarProps);
    }
  };

  const name = (
    <Typography variant="subtitle1" noWrap sx={{ maxWidth: 220 }}>
      {active.businessAccountName}
    </Typography>
  );

  if (others.length === 0) {
    return (
      <Box
        data-testid="account-switcher"
        data-account-id={active.businessAccountId}
        sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, px: 1.5, color: 'secondary.main' }}
      >
        <Buildings size={20} variant="Bold" />
        {name}
      </Box>
    );
  }

  return (
    <Box sx={{ flexShrink: 0 }}>
      <Button
        data-testid="account-switcher"
        data-account-id={active.businessAccountId}
        aria-haspopup="true"
        aria-controls={anchorEl ? 'account-switcher-menu' : undefined}
        aria-label={t('accountSwitcher.label', { defaultValue: 'Business account' })}
        onClick={(event: MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget)}
        disabled={switching}
        variant="text"
        color="secondary"
        startIcon={<Buildings variant="Bold" size={20} />}
        endIcon={<ArrowDown2 size={16} />}
        sx={(theme) => ({
          height: theme.spacing(5.5),
          px: 1.5,
          borderRadius: 1,
          textTransform: 'none',
          color: 'secondary.main',
          bgcolor: 'secondary.100',
          '&:hover': { bgcolor: 'secondary.200' },
          ...theme.applyStyles('dark', { bgcolor: 'background.default', '&:hover': { bgcolor: 'background.paper' } })
        })}
      >
        {name}
      </Button>
      <Menu
        id="account-switcher-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {others.map((account) => (
          <MenuItem
            key={account.businessAccountId}
            data-testid="account-switcher-option"
            data-account-id={account.businessAccountId}
            onClick={() => handleChoose(account)}
          >
            <ListItemText primary={account.businessAccountName} secondary={roleLabel(account)} />
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}
